/**
 * 시딩 제안형 상세 — service DTO → presentation `SeedingCampaignDetail` 매핑.
 *
 * service 의 `screenState`(7종) 는 화면 `status`(7종) 와 1:1. 나머지 표시 필드는
 * `seedingViewCommon` 헬퍼로 파생한다. BE 미제공 copy/필드는 안전 fallback + NOTE.
 */

import type {
  GetSeedingProposalDetailServiceResponse,
  SeedingProposalScreenState,
} from '@musinsa/services-curator';
import type {
  SeedingCampaignDetail,
  SeedingCampaignInfo,
  SeedingContentTypeData,
  SeedingPrimaryCta,
  SeedingProposeStatus,
} from '../types';
import {
  applyPenaltyToCta,
  buildBrand,
  buildContentGuide,
  buildContentSubmission,
  buildEditableContentType,
  buildFrozenContentType,
  buildPenalty,
  buildShippingAddress,
  buildShippingTracking,
  contentTypeLabel,
  formatDateDot,
  formatProposeShippingPlanned,
  mapGoodsToProducts,
  parseKstDateTime,
  snsChannelLabel,
  toPresentationContentType,
} from './seedingViewCommon';

// 문자열 리터럴 키 (services 런타임 import 회피 — recruit mapper 동일 사유).
const STATUS_BY_SCREEN_STATE: Record<SeedingProposalScreenState, SeedingProposeStatus> = {
  PENDING: 'pending',
  PRODUCT_SELECTED: 'productSelected',
  PREPARING: 'preparing',
  SHIPPING: 'shipping',
  DELIVERED: 'delivered',
  CONTENT_SUBMITTED: 'contentRegistered',
  SOLD_OUT_UNAVAILABLE: 'soldOut',
  REJECTED: 'rejected',
};

/** 수락 전(검토) 단계 — 옵션 선택 가능. */
const ACCEPT_STAGE_STATUSES = new Set<SeedingProposeStatus>([
  'pending',
  'productSelected',
  'soldOut',
]);
/**
 * 배송지 변경 가능 단계 — 제안형은 **수락 전(pending/productSelected)까지만** 변경 가능.
 * 수락 시점에 주문이 생성되므로 preparing(수락 후 상품 준비중)부터는 변경 불가.
 * (SID-4116 reopen — 수락 후에도 노출되던 버그 정정. 세션 중 추가했던 'preparing' 제거.
 *  SID-4150 Figma 는 수락완료 상세 레이아웃이며 기능 정책은 SID-4116(주문 생성) 우선.)
 */
const EDITABLE_ADDRESS_STATUSES = new Set<SeedingProposeStatus>(['pending', 'productSelected']);

/** 제안 번호 노출 단계 — 수락 직후(=상품 준비중) 이후. (SID-4331) */
const PROPOSAL_NUMBER_STATUSES = new Set<SeedingProposeStatus>([
  'preparing',
  'shipping',
  'delivered',
  'contentRegistered',
]);

const buildPrimaryCta = (status: SeedingProposeStatus): SeedingPrimaryCta | null => {
  if (status === 'pending' || status === 'productSelected') {
    return {
      mode: 'dual',
      // CTA 라벨: 수락하기→수락 / 거절하기→거절. (SID-4327 수정4)
      secondary: { label: '거절', action: 'REJECT', disabled: false },
      // 수락 CTA 는 항상 enabled — 옵션/동의 등 유효성은 클릭 시점에 검사·안내한다. (SID-4163)
      primary: { label: '수락', action: 'ACCEPT', disabled: false },
    };
  }
  if (status === 'soldOut') {
    return {
      mode: 'single',
      variant: 'blur',
      // '확인' 클릭 시 직전 페이지로 복귀(router.back) — 메인 이동 버그 수정. (SID-4166)
      primary: { label: '확인', action: 'GO_BACK', disabled: false },
    };
  }
  return null;
};

const buildCountdown = (
  status: SeedingProposeStatus,
  data: GetSeedingProposalDetailServiceResponse,
): SeedingCampaignDetail['countdown'] => {
  const { participation } = data;
  if (ACCEPT_STAGE_STATUSES.has(status)) {
    // 제안형 수락 마감 = participation.responseDeadlineAt (48h, SID-2659). campaign.recruitmentEndAt 은
    // 모집형 전용이라 제안형에선 null.
    const deadline = participation.responseDeadlineAt;
    return {
      label: '수락까지 남은 시간',
      targetAt: deadline ? parseKstDateTime(deadline) : null,
    };
  }
  // 콘텐츠 등록 완료(제출완료) — 카운트다운 숨기고 완료 텍스트만 H1 자리에 노출. (SID-4178)
  if (status === 'contentRegistered') {
    return { label: '콘텐츠 등록 완료', targetAt: null };
  }
  if (status === 'delivered') {
    const deadline = participation.contentSubmissionDeadlineAt;
    return {
      label: '콘텐츠 등록까지 남은 시간',
      targetAt: deadline ? parseKstDateTime(deadline) : null,
      // 기한 경과 시 카운트다운 숨기고 경과 텍스트만 노출. (SID-4178 작업2, Figma 2194-107052)
      expiredLabel: '콘텐츠 제출 기한 경과',
    };
  }
  return null;
};

const buildContentType = (
  status: SeedingProposeStatus,
  data: GetSeedingProposalDetailServiceResponse,
): SeedingContentTypeData => {
  const { campaign, participation } = data;
  const selectedContentType = participation.selectedContentType;
  if (ACCEPT_STAGE_STATUSES.has(status) || selectedContentType === null) {
    // 콘텐츠 유형 default = 정렬된 선택 가능 유형의 첫 번째 (buildEditableContentType 가 채움). (SID-4138/4393)
    const editable = buildEditableContentType(
      campaign,
      selectedContentType ? toPresentationContentType(selectedContentType) : null,
      participation.messageToBrand ?? '',
    );
    // 제안형 전용 free-form placeholder. (SID-4140)
    return {
      ...editable,
      messagePlaceholder:
        '제안에 감사 인사를 전하거나, 나만의 스타일링 강점 등을 자유롭게 적어주세요.',
    };
  }
  return buildFrozenContentType(
    campaign,
    selectedContentType,
    participation.messageToBrand ?? '',
    // 배송완료(delivered) 이후에만 실제 마감일("YYYY-MM-DD까지 업로드"), 그 전(준비중/배송중)엔 정책 문구. (SID-4319)
    status === 'delivered' || status === 'contentRegistered'
      ? (participation.contentSubmissionDeadlineAt ?? null)
      : null,
  );
};

const buildCampaignInfo = (
  status: SeedingProposeStatus,
  data: GetSeedingProposalDetailServiceResponse,
): SeedingCampaignInfo => {
  const { campaign, participation, fulfillments, estimatedShippingDeadlineAt } = data;
  const contentTypeLabelList = campaign.contentTypes.map((option) => contentTypeLabel(option.type));
  const contentTypeLabels = contentTypeLabelList.join(', ');
  // 콘텐츠 유형이 복수일 때만 '중 택 1' postfix (단일이면 없음). (노드 211)
  const contentTypeChoiceSuffix = contentTypeLabelList.length > 1 ? ' 중 택 1' : '';
  // 발송 시작 후(fulfillments 존재)엔 확정값 우선, 그 전엔 BE 추정값. (API 0615)
  const shippingPlannedDate =
    fulfillments[0]?.shippingDeadlineAt ?? estimatedShippingDeadlineAt ?? null;
  // 제안형 발송 예정일: 수락전(pending/productSelected)·수락완료(preparing) = "YYYY.MM.DD (D) 이내",
  // 배송중(shipping) = "YY.MM.DD (D) 예정". (SID-4327)
  const shippingPlannedBefore = formatProposeShippingPlanned(shippingPlannedDate, 'full', '이내');

  if (status === 'pending' || status === 'soldOut') {
    const snsRow = {
      label: 'SNS 채널',
      value: campaign.snsChannels.map(snsChannelLabel).join(', '),
    };
    const contentTypeRow = {
      label: '콘텐츠 유형',
      value: `${contentTypeLabels}${contentTypeLabelList.length > 1 ? ' 중 택1' : ''}`,
    };
    return {
      title: `콘텐츠 유형 : ${contentTypeLabels}${contentTypeChoiceSuffix}`,
      description: '제공된 모든 상품을 착용/연출한 콘텐츠를 업로드해야 합니다.',
      // 수락 flow(pending): 발송 예정일 → SNS 채널 → 콘텐츠 유형 (응답 마감 제거). (SID-4327 수정1)
      // soldOut: 기존 SNS 채널 → 응답 마감 → 콘텐츠 유형 유지.
      infoRows:
        status === 'pending'
          ? [{ label: '발송 예정일', value: shippingPlannedBefore }, snsRow, contentTypeRow]
          : [
              snsRow,
              {
                label: '응답 마감',
                value: participation.responseDeadlineAt
                  ? formatDateDot(participation.responseDeadlineAt)
                  : '-',
              },
              contentTypeRow,
            ],
    };
  }

  if (status === 'productSelected') {
    const selectedTypeLabel = participation.selectedContentType
      ? contentTypeLabel(participation.selectedContentType)
      : '';
    return {
      title: '제안 정보',
      description: '옵션이 선택되었습니다. 수락을 눌러 캠페인을 시작하세요.',
      // 수락전(productSelected): 발송 예정일 "이내" 노출. 콘텐츠 유형은 최하단. (SID-4327 / SID-4142)
      infoRows: [
        { label: '발송 예정일', value: shippingPlannedBefore },
        { label: 'SNS 채널', value: campaign.snsChannels.map(snsChannelLabel).join(', ') },
        { label: '선정 인원', value: '1명 (단독 제안)' },
        {
          label: '응답 마감',
          value: participation.responseDeadlineAt
            ? formatDateDot(participation.responseDeadlineAt)
            : '-',
        },
        { label: '콘텐츠 유형', value: selectedTypeLabel },
      ],
    };
  }

  const frozenLabel = participation.selectedContentType
    ? contentTypeLabel(participation.selectedContentType)
    : '';
  // 발송 시작 후(fulfillments 존재)엔 확정값 우선, 그 전엔 BE 추정값. (API 0615)
  // 수락완료(preparing) = "YYYY.MM.DD (D) 이내", 배송중(shipping) = "YY.MM.DD (D) 예정". (SID-4327)
  const infoRows =
    status === 'preparing'
      ? [{ label: '발송 예정일', value: shippingPlannedBefore }]
      : status === 'shipping'
        ? [
            {
              label: '발송 예정일',
              value: formatProposeShippingPlanned(shippingPlannedDate, 'short', '예정'),
            },
          ]
        : [];

  return {
    title: `콘텐츠 유형 : ${frozenLabel}`,
    description: '제공된 모든 상품을 착용/연출한 콘텐츠를 업로드해야 합니다.',
    infoRows,
  };
};

export const mapProposalDetailToView = (
  data: GetSeedingProposalDetailServiceResponse,
): SeedingCampaignDetail => {
  const { campaign, goods, participation, fulfillments, contentSubmissions, deliveryAddress } =
    data;
  const status = STATUS_BY_SCREEN_STATE[data.screenState];
  const penalty = buildPenalty(data.penalty);

  const actionVerb: '선택' | '변경' | null =
    status === 'pending' ? '선택' : status === 'productSelected' ? '변경' : null;
  const isCompleted = participation.status === 'COMPLETED';

  return {
    campaignId: campaign.id,
    participationId: participation.id,
    kind: 'propose',
    status,
    campaignName: campaign.title,
    brand: buildBrand(campaign),
    countdown: buildCountdown(status, data),
    campaignInfo: buildCampaignInfo(status, data),
    // NOTE: BE DTO 미제공 — 선택 옵션 텍스트. "선택한 옵션 요약" 표는 값을 채울 수 없어 생략.
    applicationInfo: null,
    products: mapGoodsToProducts(goods, {
      // 거절(종료)은 읽기전용이나 카테고리 라벨은 노출 (Figma 2276-34571). actionVerb=null → 옵션 버튼 없음.
      showCategory: ACCEPT_STAGE_STATUSES.has(status) || status === 'rejected',
      actionVerb,
    }),
    productSectionGuide:
      status === 'pending'
        ? '모든 상품의 옵션을 선택해 주세요.'
        : status === 'productSelected'
          ? `옵션 선택 완료 — 총 ${goods.length}개 상품`
          : status === 'soldOut'
            ? '브랜드사 사정으로 인한 상품 품절로, 부득이하게 캠페인 진행이 불가합니다. 너른 양해 부탁드립니다.'
            : null,
    productSectionGuideEmphasis: status === 'soldOut' ? 'red' : undefined,
    contentType: buildContentType(status, data),
    contentGuide: buildContentGuide(campaign),
    shippingTracking: buildShippingTracking(fulfillments, contentSubmissions),
    contentSubmission: buildContentSubmission(
      campaign,
      fulfillments,
      contentSubmissions,
      participation.selectedContentType,
      isCompleted,
    ),
    // 제안 번호 = participation.id (모집형 '신청 번호'와 동일 값, 타이틀만 상이). (SID-4331)
    selectionNumber:
      PROPOSAL_NUMBER_STATUSES.has(status) && participation ? String(participation.id) : null,
    shippingAddress: buildShippingAddress(deliveryAddress, EDITABLE_ADDRESS_STATUSES.has(status)),
    rejected: null,
    penalty,
    primaryCta: applyPenaltyToCta(buildPrimaryCta(status), penalty !== null),
    requiredSnsChannels: campaign.snsChannels,
  };
};
